<template>
  <div class="common-report-table">
    <div v-for="(item, index) in commonReportTableInfo.components" :key="index">
      <el-card v-if="item.conditions && item.conditions.length > 0" class="query-card">
        <div slot="header" class="flex justify-between">
          <span>{{ $t('commonReportTable.queryConditions') }}</span>
        </div>
        <el-row :gutter="20">
          <el-col v-for="(conditionItem, conditionIndex) in item.conditions" :key="(item.componentId || index) + '-' + (conditionItem.param || conditionIndex)" :span="6" class="margin-bottom">
            <div class="condition-item">
              <div class="condition-label">{{ conditionItem.name }}</div>
              <el-date-picker
                v-if="conditionItem.type === 'date'"
                :value="conditionItem.value"
                type="date"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                :placeholder="conditionItem.holdpace"
                style="width: 100%"
                clearable
                @input="val => setConditionValue(conditionItem, val)"
              />
              <el-select
                v-else-if="conditionItem.type === 'select'"
                :value="conditionItem.value"
                :placeholder="conditionItem.holdpace"
                style="width: 100%"
                clearable
                @input="val => setConditionValue(conditionItem, val)"
                @change="val => setConditionValue(conditionItem, val)"
              >
                <el-option
                  v-for="opt in (conditionItem.options || [])"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
              <el-input
                v-else
                :value="conditionItem.value"
                :placeholder="conditionItem.holdpace"
                clearable
                @input="val => setConditionValue(conditionItem, val)"
              />
            </div>
          </el-col>
          <el-col :span="6">
            <el-button type="primary" @click="pagination.current = 1; _queryReportTableMethod(item)">
              <i class="el-icon-search"></i>
              {{ $t('commonReportTable.query') }}
            </el-button>
            <el-button @click="_resetReportTableMethod(item)">
              <i class="el-icon-refresh"></i>
              {{ $t('commonReportTable.reset') }}
            </el-button>
          </el-col>
        </el-row>
      </el-card>

      <el-card class="table-card">
        <div slot="header" class="flex justify-between">
          <span>{{ item.componentName }}</span>
          <div style="float: right;">
            <el-button type="primary" size="small" @click="_exportReportTableMethod(item)">
              <i class="el-icon-download"></i>
              {{ $t('commonReportTable.export') }}
            </el-button>
            <el-button type="primary" size="small" @click="_printReportTableMethod(item)">
              <i class="el-icon-printer"></i>
              {{ $t('commonReportTable.print') }}
            </el-button>
          </div>
        </div>

        <div v-if="hasFooter(item)" class="footer-wrapper">
          <div
            v-for="(tmpItemTh, key) in item.footer"
            :key="key"
            class="footer-item"
            :class="{ 'is-undo': isUndoStat(key, tmpItemTh) }"
          >
            <span class="footer-label">{{ key }}</span>
            <span class="footer-value">{{ tmpItemTh }}</span>
          </div>
        </div>

        <el-table :data="item.data" border style="width: 100%" v-loading="loading"
          :span-method="({ column, rowIndex }) => groupSpanMethod(item.data, column, rowIndex)">
          <el-table-column v-for="col in displayColumns(item)" :key="col.key" :prop="col.key" :label="col.label"
            :min-width="col.minWidth" :fixed="col.fixed" show-overflow-tooltip align="center">
            <template slot-scope="scope">
              <div :class="cellClass(col.key, scope.row[col.key])">
                {{ scope.row[col.key] }}
              </div>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination :current-page.sync="pagination.current" :page-sizes="[10, 20, 30, 50]" :page-size="pagination.size"
          :total="pagination.total" layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
          @current-change="handleCurrentChange" />
      </el-card>
    </div>
  </div>
</template>

<script>
import {
  listReportCustomComponentRel,
  listReportCustomComponentCondition,
  listReportCustomComponentData,
  listReportCustomComponentDataFooter,
  exportData
} from '@/api/report/commonReportApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'CommonReportTable',
  data() {
    return {
      commonReportTableInfo: {
        components: [],
        customId: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      loading: false,
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
  },
  methods: {
    displayHeaders(item) {
      if (!item || !item.th) {
        return this.inspectionFallbackHeaders(item)
      }
      const headers = item.th.filter(th => th !== null && th !== undefined && String(th).trim() !== '' && !String(th).startsWith('_'))
      return headers.length > 0 ? headers : this.inspectionFallbackHeaders(item)
    },
    inspectionFallbackHeaders(item) {
      if (this.isPointStatistics(item)) {
        return ['巡检点', '位置', '应巡', '已巡', '未巡', '员工', '任务名称', '任务时间', '打卡时间', '巡检状态', '签到状态', '巡检情况', '巡检说明']
      }
      if (this.isTodayInspection(item)) {
        return ['员工', '任务名称', '任务时间', '应巡', '已巡', '未巡', '巡检点', '位置', '打卡时间', '巡检状态', '签到状态', '巡检情况', '巡检说明']
      }
      return []
    },
    headerMinWidth(label) {
      const widths = {
        员工: 110,
        任务名称: 150,
        任务时间: 340,
        巡检点: 140,
        位置: 140,
        应巡: 72,
        已巡: 72,
        未巡: 72,
        打卡时间: 170,
        巡检状态: 90,
        签到状态: 90,
        巡检情况: 90,
        巡检说明: 160
      }
      return widths[label] || 120
    },
    displayColumns(item) {
      let freezeLabel = ''
      if (this.isPointStatistics(item)) {
        freezeLabel = '巡检点'
      } else if (this.isTodayInspection(item)) {
        freezeLabel = '员工'
      }
      return this.displayHeaders(item).map(label => ({
        key: label,
        label,
        minWidth: this.headerMinWidth(label),
        fixed: label === freezeLabel ? 'left' : false
      }))
    },
    groupValue(row, column) {
      if (!row) {
        return ''
      }
      if (['巡检点', '位置', '应巡', '已巡', '未巡'].includes(column.property) && row._inspectionId) {
        return 'p:' + row._inspectionId
      }
      if (['应巡', '已巡', '未巡'].includes(column.property) && row._taskId) {
        return 't:' + row._taskId
      }
      if (['员工', '任务名称', '任务时间'].includes(column.property) && row._taskId) {
        // 同一任务会覆盖多个点；巡检点统计必须带上点 ID，避免相邻两个点被合成一组
        return row._inspectionId ? ('p:' + row._inspectionId + '|t:' + row._taskId) : ('t:' + row._taskId)
      }
      return ''
    },
    groupSpanMethod(data, column, rowIndex) {
      if (!data || !data[rowIndex]) {
        return [1, 1]
      }
      const groupValue = this.groupValue(data[rowIndex], column)
      if (!groupValue) {
        return [1, 1]
      }
      if (rowIndex > 0 && this.groupValue(data[rowIndex - 1], column) === groupValue) {
        return [0, 0]
      }
      let rowspan = 1
      for (let i = rowIndex + 1; i < data.length; i++) {
        if (this.groupValue(data[i], column) === groupValue) {
          rowspan++
        } else {
          break
        }
      }
      return [rowspan, 1]
    },
    isTodayInspection(component) {
      return component && (component.componentName === '今日巡检' || component.componentId === '102022051133910002')
    },
    isPointStatistics(component) {
      return component && (component.componentName === '巡检点统计' || component.componentId === '102022051120700639')
    },
    isInspectionReport(component) {
      return this.isTodayInspection(component) || this.isPointStatistics(component)
    },
    cellClass(key, value) {
      if (key === '任务时间') {
        return 'time-full'
      }
      if ((key === '未巡' || key === '未巡检') && Number(value) > 0) {
        return 'stat-undo'
      }
      if (key === '巡检状态' && value === '未巡检') {
        return 'stat-undo'
      }
      return 'text-auto'
    },
    hasFooter(item) {
      return item && item.footer && Object.keys(item.footer).length > 0
    },
    isUndoStat(key, value) {
      return (key === '未巡检' || key === '未巡') && Number(value) > 0
    },
    formatToday() {
      const d = new Date()
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    },
    conditionParams(item) {
      const condition = {}
      ;(item.conditions || []).forEach(_item => {
        condition[_item.param] = _item.value
      })
      if (this.isTodayInspection(item) && condition.startTime) {
        condition.endTime = condition.startTime
      }
      return condition
    },
    wrapCondition(cond) {
      const next = Object.assign({}, cond)
      if (next.value == null) {
        next.value = ''
      }
      return next
    },
    setConditions(component, list) {
      this.$set(component, 'conditions', (list || []).map(cond => this.wrapCondition(cond)))
    },
    setConditionValue(conditionItem, val) {
      this.$set(conditionItem, 'value', val == null ? '' : val)
    },
    applyInspectionDefaultDates(component) {
      if (!this.isTodayInspection(component) && !this.isPointStatistics(component)) {
        return
      }
      const today = this.formatToday()
      ;(component.conditions || []).forEach(cond => {
        if (this.isTodayInspection(component)) {
          if (cond.param === 'startTime' && !cond.value) {
            this.setConditionValue(cond, today)
          }
          return
        }
        if ((cond.param === 'startTime' || cond.param === 'endTime') && !cond.value) {
          this.setConditionValue(cond, today)
        }
      })
    },
    inspectionConditionExtras(component, today) {
      const staffAndState = [
        { name: '员工', param: 'staffName', type: 'text', holdpace: '请输入员工姓名', value: '' },
        { name: '任务名称', param: 'planName', type: 'text', holdpace: '请输入任务/计划名称', value: '' },
        { name: '巡检点', param: 'inspectionName', type: 'text', holdpace: '请输入巡检点', value: '' },
        {
          name: '状态',
          param: 'inspectState',
          type: 'select',
          holdpace: '全部',
          value: '',
          options: [
            { label: '全部', value: '' },
            { label: '已巡检', value: 'done' },
            { label: '未巡检', value: 'undo' }
          ]
        }
      ]
      if (this.isTodayInspection(component)) {
        return [
          { name: '日期', param: 'startTime', type: 'date', holdpace: '请选择日期', value: today }
        ].concat(staffAndState)
      }
      return [
        { name: '开始时间', param: 'startTime', type: 'date', holdpace: '任务开始日期', value: today },
        { name: '结束时间', param: 'endTime', type: 'date', holdpace: '任务结束日期', value: today }
      ].concat(staffAndState)
    },
    ensureTodayInspectionConditions(component) {
      if (!this.isTodayInspection(component) && !this.isPointStatistics(component)) {
        return
      }
      const today = this.formatToday()
      const extras = this.inspectionConditionExtras(component, today)
      const existing = component.conditions || []
      const byParam = {}
      existing.forEach(cond => {
        byParam[cond.param] = cond
      })
      const merged = extras.map(extra => {
        const old = byParam[extra.param]
        if (!old) {
          return extra
        }
        if (!Object.prototype.hasOwnProperty.call(old, 'value')) {
          this.$set(old, 'value', '')
        }
        if (extra.type === 'select') {
          old.type = 'select'
          this.$set(old, 'options', extra.options)
        } else if (extra.type === 'date' && (!old.type || old.type === 'text')) {
          old.type = 'date'
        }
        if ((extra.param === 'startTime' || extra.param === 'endTime') && !old.value) {
          this.$set(old, 'value', today)
        }
        if (extra.name && old.name !== extra.name) {
          old.name = extra.name
        }
        if (extra.holdpace) {
          old.holdpace = extra.holdpace
        }
        return old
      })
      existing.forEach(cond => {
        if (this.isTodayInspection(component) && cond.param === 'endTime') {
          return
        }
        if (!extras.some(extra => extra.param === cond.param)) {
          merged.push(cond)
        }
      })
      this.setConditions(component, merged)
    },
    handleSwitch(value) {
      this.commonReportTableInfo.customId = value.customId
      this.pagination.current = 1
      this._listReportCustomTableComponent()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this._queryReportTableMethod(this.commonReportTableInfo.components[0])
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._queryReportTableMethod(this.commonReportTableInfo.components[0])
    },
    async _queryReportTableMethod(item) {
      const condition = this.conditionParams(item)
      await this._listReportCustomTableDatas(this.pagination.current, this.pagination.size, item, condition)
      await this._listReportCustomTableFooter(this.pagination.current, this.pagination.size, item, condition)
    },
    _resetReportTableMethod(item) {
      item.conditions.forEach(_item => {
        this.setConditionValue(_item, '')
      })
      this.applyInspectionDefaultDates(item)
      const condition = this.conditionParams(item)
      this.pagination.current = 1
      this._listReportCustomTableDatas(1, this.pagination.size, item, condition)
      this._listReportCustomTableFooter(1, this.pagination.size, item, condition)
    },
    async _listReportCustomTableComponent() {
      try {
        this.loading = true
        const params = {
          page: 1,
          row: this.pagination.size,
          customId: this.commonReportTableInfo.customId,
          componentType: '1001'
        }
        const { data } = await listReportCustomComponentRel(params)
        this.commonReportTableInfo.components = data
        for (const item of this.commonReportTableInfo.components) {
          await this._listReportCustomTableConditions(item)
          const condition = this.conditionParams(item)
          await this._listReportCustomTableDatas(1, this.pagination.size, item, condition)
          await this._listReportCustomTableFooter(1, this.pagination.size, item, condition)
        }
      } catch (error) {
        console.error('Failed to load report components:', error)
      } finally {
        this.loading = false
      }
    },
    async _listReportCustomTableConditions(component) {
      try {
        const params = {
          page: 1,
          row: this.pagination.size,
          componentId: component.componentId
        }
        const { data } = await listReportCustomComponentCondition(params)
        this.setConditions(component, data || [])
        this.ensureTodayInspectionConditions(component)
        this.applyInspectionDefaultDates(component)
      } catch (error) {
        console.error('Failed to load conditions:', error)
      }
    },
    async _listReportCustomTableDatas(page, row, component, conditions) {
      try {
        this.loading = true
        const params = {
          page,
          row,
          componentId: component.componentId,
          communityId: this.communityId,
          ...conditions
        }
        const { data, total } = await listReportCustomComponentData(params)
        this.$set(component, 'th', this.displayHeaders({ th: data.th, componentId: component.componentId, componentName: component.componentName }))
        this.$set(component, 'data', data.td)
        if (data.footer) {
          this.$set(component, 'footer', data.footer)
        }
        this.pagination.total = total
      } catch (error) {
        console.error('Failed to load table data:', error)
      } finally {
        this.loading = false
      }
    },
    async _listReportCustomTableFooter(page, row, component, conditions) {
      if (this.isInspectionReport(component)) {
        return
      }
      try {
        const params = {
          page,
          row,
          componentId: component.componentId,
          communityId: this.communityId,
          ...conditions
        }
        const { data } = await listReportCustomComponentDataFooter(params)
        component.footer = data
      } catch (error) {
        console.error('Failed to load footer data:', error)
      }
    },
    async _exportReportTableMethod(item) {
      try {
        const condition = this.conditionParams(item)
        const params = {
          page: 1,
          row: 10000,
          componentId: item.componentId,
          communityId: this.communityId,
          pagePath: 'exportCustomReportTableData',
          ...condition
        }
        const { code, msg } = await exportData(params)
        this.$message.success(msg)
        if (code === 0) {
          this.$router.push('/pages/property/downloadTempFile?tab=downloadCenter')
        }
      } catch (error) {
        console.error('Export failed:', error)
      }
    },
    _printReportTableMethod(data) {
      localStorage.setItem('printCommonReportTableData', JSON.stringify(data))
      window.open('/#/pages/property/printCommonReportTable')
    }
  }
}
</script>

<style lang="scss" scoped>
.common-report-table {
  .query-card {
    margin-bottom: 20px;

    .condition-item {
      .condition-label {
        margin-bottom: 6px;
        color: #606266;
        font-size: 13px;
      }
    }
  }

  .table-card {
    margin-bottom: 20px;

    ::v-deep .el-table {
      .el-table__body-wrapper {
        overflow-x: auto;
      }
    }
  }

  .text-auto {
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .time-full {
    white-space: nowrap;
  }

  .stat-undo {
    color: #f56c6c;
    font-weight: 600;
  }

  .footer-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 0 0 16px;
    padding: 12px;
    background-color: #f5f7fa;
    border-radius: 4px;

    .footer-item {
      min-width: 120px;
      padding: 8px 12px;
      background: #fff;
      border-radius: 4px;
      border: 1px solid #ebeef5;

      .footer-label {
        display: block;
        margin-bottom: 4px;
        color: #909399;
        font-size: 12px;
      }

      .footer-value {
        color: #303133;
        font-size: 18px;
        font-weight: 600;
      }

      &.is-undo {
        border-color: #fbc4c4;
        background: #fef0f0;

        .footer-value {
          color: #f56c6c;
        }
      }
    }
  }

  .el-pagination {
    margin-top: 20px;
    text-align: right;
  }
}
</style>